import React, { useState, Fragment, Component } from 'react';
import { ChevronDown, ChevronRight, Award, Building, AlertTriangle, Package, DollarSign, Wine, Grape, Calendar, MapPin } from 'lucide-react';
export function BOMDetailsCard() {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const grapeVarietals = [{
    varietal: 'Cabernet Sauvignon',
    percentage: '85%',
    unitCost: '$3.65',
    extendedCost: '$3.65',
    vineyard: 'Alexander Valley Estate',
    region: 'Alexander Valley AVA',
    vintage: '2019',
    certifications: ['Organic Certified', 'Sustainable Farming']
  }, {
    varietal: 'Merlot',
    percentage: '10%',
    unitCost: '$0.42',
    extendedCost: '$0.42',
    vineyard: 'Russian River Valley',
    region: 'Russian River Valley AVA',
    vintage: '2019',
    certifications: ['Sustainable Farming']
  }, {
    varietal: 'Petit Verdot',
    percentage: '5%',
    unitCost: '$0.25',
    extendedCost: '$0.25',
    vineyard: 'Knights Valley Estate',
    region: 'Knights Valley AVA',
    vintage: '2019',
    certifications: ['Sustainable Farming']
  }];
  const packagingComponents = [{
    component: 'Bordeaux Wine Bottle',
    specification: '750ml Premium Glass',
    quantity: '1 unit',
    unitCost: '$2.85',
    extendedCost: '$2.85',
    supplier: 'Saverglass',
    certifications: ['Eco-Certified', 'Recycled Content'],
    specifications: '750ml antique green glass, extra deep punt, 17.5oz weight'
  }, {
    component: 'Natural Cork',
    specification: '45mm Premium Grade',
    quantity: '1 unit',
    unitCost: '$0.95',
    extendedCost: '$0.95',
    supplier: 'Amorim Cork',
    certifications: ['FSC Certified', 'TCA Tested'],
    specifications: '45mm length, premium quality, TCA-free guarantee'
  }, {
    component: 'Capsule',
    specification: 'Tin Capsule',
    quantity: '1 unit',
    unitCost: '$0.45',
    extendedCost: '$0.45',
    supplier: 'Ramondin',
    certifications: ['Lead-Free'],
    specifications: 'Tin alloy, deep burgundy color with gold embossing'
  }, {
    component: 'Label Set',
    specification: 'Front & Back',
    quantity: '1 set',
    unitCost: '$1.25',
    extendedCost: '$1.25',
    supplier: 'CCL Label',
    certifications: ['FSC Certified'],
    specifications: 'Estate series design, textured paper, gold foil accents'
  }];
  const wineProcessingDetails = [{
    process: 'Aging',
    details: '18 months in French oak barrels (60% new)',
    unitCost: '$8.75',
    supplier: 'Tonnellerie Taransaud'
  }, {
    process: 'Bottling',
    details: 'Estate bottled on June 15, 2021',
    unitCost: '$1.20',
    supplier: 'In-house bottling line'
  }, {
    process: 'Fining & Filtration',
    details: 'Light egg white fining, sterile filtration',
    unitCost: '$0.85',
    supplier: 'Various'
  }];
  const totalGrapeCost = grapeVarietals.reduce((sum, item) => sum + parseFloat(item.extendedCost.replace('$', '').replace(',', '')), 0);
  const totalPackagingCost = packagingComponents.reduce((sum, item) => sum + parseFloat(item.extendedCost.replace('$', '').replace(',', '')), 0);
  const totalProcessingCost = wineProcessingDetails.reduce((sum, item) => sum + parseFloat(item.unitCost.replace('$', '').replace(',', '')), 0);
  const toggleExpanded = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };
  return <div className="space-y-6" data-id="element-23">
      {/* Packaging Components Section - Now First */}
      <div className="bg-white rounded-lg border" data-id="element-24">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between cursor-pointer" onClick={() => toggleExpanded('packaging')} data-id="element-25">
          <div className="flex items-center space-x-2" data-id="element-26">
            <Package className="w-5 h-5 text-gray-600" data-id="element-27" />
            <div data-id="element-28">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-29">
                Packaging Components
              </h2>
              <p className="text-sm text-gray-600 mt-1" data-id="element-30">
                Bottle, closure, and label details
              </p>
            </div>
          </div>
          {expandedSection === 'packaging' ? <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-31" /> : <ChevronRight className="w-5 h-5 text-gray-400" data-id="element-32" />}
        </div>
        {(expandedSection === 'packaging' || expandedSection === null) && <div className="overflow-x-auto" data-id="element-33">
            <table className="w-full" data-id="element-34">
              <thead className="bg-gray-50" data-id="element-35">
                <tr data-id="element-36">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-37">
                    Component
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-38">
                    Specification
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-39">
                    Quantity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-40">
                    Unit Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-41">
                    Extended Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-42">
                    Supplier
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-43">
                {packagingComponents.map(item => <tr key={item.component} className="hover:bg-gray-50" data-id="element-44">
                    <td className="px-6 py-4" data-id="element-45">
                      <div className="flex items-center" data-id="element-46">
                        <div className="text-sm font-medium text-gray-900" data-id="element-47">
                          {item.component}
                        </div>
                        {item.certifications.some(cert => cert.includes('Eco')) && <Award className="w-4 h-4 text-green-600 ml-2" data-id="element-48" />}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-49">
                      {item.specification}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-50">
                      {item.quantity}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-51">
                      {item.unitCost}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-52">
                      {item.extendedCost}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600" data-id="element-53">
                      {item.supplier}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>
      {/* Grape Composition Section */}
      <div className="bg-white rounded-lg border" data-id="element-54">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between cursor-pointer" onClick={() => toggleExpanded('grapes')} data-id="element-55">
          <div className="flex items-center space-x-2" data-id="element-56">
            <Grape className="w-5 h-5 text-gray-600" data-id="element-57" />
            <div data-id="element-58">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-59">
                Grape Composition
              </h2>
              <p className="text-sm text-gray-600 mt-1" data-id="element-60">
                Varietal breakdown and cost per bottle
              </p>
            </div>
          </div>
          {expandedSection === 'grapes' ? <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-61" /> : <ChevronRight className="w-5 h-5 text-gray-400" data-id="element-62" />}
        </div>
        {expandedSection === 'grapes' && <div className="overflow-x-auto" data-id="element-63">
            <table className="w-full" data-id="element-64">
              <thead className="bg-gray-50" data-id="element-65">
                <tr data-id="element-66">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-67">
                    Varietal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-68">
                    Percentage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-69">
                    Vineyard
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-70">
                    Region
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-71">
                    Vintage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-72">
                    Unit Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-73">
                    Extended Cost
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-74">
                {grapeVarietals.map(item => <tr key={item.varietal} className="hover:bg-gray-50" data-id="element-75">
                    <td className="px-6 py-4" data-id="element-76">
                      <div className="flex items-center" data-id="element-77">
                        <div className="text-sm font-medium text-gray-900" data-id="element-78">
                          {item.varietal}
                        </div>
                        <div className="flex ml-2" data-id="element-79">
                          {item.certifications.some(cert => cert.includes('Organic')) && <Award className="w-4 h-4 text-green-600 ml-1" data-id="element-80" />}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-81">
                      {item.percentage}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-82">
                      {item.vineyard}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-83">
                      {item.region}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-84">
                      {item.vintage}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-85">
                      {item.unitCost}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-86">
                      {item.extendedCost}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>
      {/* Wine Processing Section */}
      <div className="bg-white rounded-lg border" data-id="element-87">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between cursor-pointer" onClick={() => toggleExpanded('processing')} data-id="element-88">
          <div className="flex items-center space-x-2" data-id="element-89">
            <Wine className="w-5 h-5 text-gray-600" data-id="element-90" />
            <div data-id="element-91">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-92">
                Wine Processing
              </h2>
              <p className="text-sm text-gray-600 mt-1" data-id="element-93">
                Aging, bottling, and processing details
              </p>
            </div>
          </div>
          {expandedSection === 'processing' ? <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-94" /> : <ChevronRight className="w-5 h-5 text-gray-400" data-id="element-95" />}
        </div>
        {expandedSection === 'processing' && <div className="overflow-x-auto" data-id="element-96">
            <table className="w-full" data-id="element-97">
              <thead className="bg-gray-50" data-id="element-98">
                <tr data-id="element-99">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-100">
                    Process
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-101">
                    Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-102">
                    Unit Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-103">
                    Supplier
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-104">
                {wineProcessingDetails.map(item => <tr key={item.process} className="hover:bg-gray-50" data-id="element-105">
                    <td className="px-6 py-4" data-id="element-106">
                      <div className="flex items-center" data-id="element-107">
                        <div className="text-sm font-medium text-gray-900" data-id="element-108">
                          {item.process}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-109">
                      {item.details}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-110">
                      {item.unitCost}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600" data-id="element-111">
                      {item.supplier}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>
      {/* Cost Summary Section */}
      <div className="bg-white rounded-lg border p-6" data-id="element-112">
        <div className="flex items-center space-x-2 mb-4" data-id="element-113">
          <DollarSign className="w-5 h-5 text-gray-600" data-id="element-114" />
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-115">
            Cost Breakdown
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-116">
          <div className="space-y-4" data-id="element-117">
            <h3 className="font-medium text-gray-900" data-id="element-118">Component Costs</h3>
            <div className="space-y-3" data-id="element-119">
              <div className="flex items-center justify-between text-sm" data-id="element-120">
                <span className="text-gray-600" data-id="element-121">Grape Cost:</span>
                <span className="font-semibold text-gray-900" data-id="element-122">
                  ${totalGrapeCost.toFixed(2)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm" data-id="element-123">
                <span className="text-gray-600" data-id="element-124">Processing Cost:</span>
                <span className="font-semibold text-gray-900" data-id="element-125">
                  ${totalProcessingCost.toFixed(2)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm" data-id="element-126">
                <span className="text-gray-600" data-id="element-127">Packaging Cost:</span>
                <span className="font-semibold text-gray-900" data-id="element-128">
                  ${totalPackagingCost.toFixed(2)}
                </span>
              </div>
              <div className="pt-4 border-t border-gray-200" data-id="element-129">
                <div className="flex items-center justify-between" data-id="element-130">
                  <span className="text-base font-medium text-gray-900" data-id="element-131">
                    Total Per Bottle Cost:
                  </span>
                  <span className="text-lg font-bold text-gray-900" data-id="element-132">
                    $
                    {(totalGrapeCost + totalPackagingCost + totalProcessingCost).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-4" data-id="element-133">
            <h3 className="font-medium text-gray-900" data-id="element-134">Unit Cost Breakdown</h3>
            <div className="space-y-3" data-id="element-135">
              {grapeVarietals.map(item => <div key={item.varietal} className="flex items-center justify-between text-sm" data-id="element-136">
                  <span className="text-gray-600" data-id="element-137">
                    {item.varietal} ({item.percentage}):
                  </span>
                  <span className="text-gray-900" data-id="element-138">{item.unitCost}</span>
                </div>)}
              <div className="border-t border-gray-200 pt-2 mt-2" data-id="element-139"></div>
              {packagingComponents.map(item => <div key={item.component} className="flex items-center justify-between text-sm" data-id="element-140">
                  <span className="text-gray-600" data-id="element-141">{item.component}:</span>
                  <span className="text-gray-900" data-id="element-142">{item.unitCost}</span>
                </div>)}
              <div className="border-t border-gray-200 pt-2 mt-2" data-id="element-143"></div>
              {wineProcessingDetails.map(item => <div key={item.process} className="flex items-center justify-between text-sm" data-id="element-144">
                  <span className="text-gray-600" data-id="element-145">{item.process}:</span>
                  <span className="text-gray-900" data-id="element-146">{item.unitCost}</span>
                </div>)}
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-6" data-id="element-147">
          Cost per bottle, excluding overhead and distribution. Based on unit
          costs for each component.
        </p>
      </div>
    </div>;
}